import { ImageTypeNameEnum, ItemTypeEnum } from '@inter-ikea-kompis/enums';
import { IProductAsset, IShoppingProduct } from '@inter-ikea-kompis/types/lib';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { captureMessage } from '@sentry/react';
import structuredClone from '@ungap/structured-clone';
import paxPanelsCatalog from 'range/catalog_pax-panels.json';
import skyttaMainCatalog from 'range/catalog_skytta.json';
import { ICatalog } from 'range/interface/catalog';
import { configurationAction } from 'redux-store/configuration/configuration.store';
import { AppDispatch, IThunkApi } from 'redux-store/store';
import { getProducts } from 'services/products/products.service';
import { IShoppingProductsMap, RangeFamilyEnum } from 'utils/constants';
import randomizeList from 'utils/randomizeList';
import { productAction } from './products.store';
import { getVpcForSpr, shoppingProducts2NormalizedTypes } from './products.utils';

interface IProductAssetModified extends IProductAsset {
  rangeFamily: string;
}

const mapCatalogProducts = (
  allProducts: IShoppingProductsMap,
  catalogData: ICatalog,
  missingIds: string[],
  customRangeFamily?: string
) => {
  return [...catalogData.products].map(product => {
    if (!product.dexf) {
      return product;
    }

    const isValid = allProducts[product.dexf]?.product.valid;
    const dexfAssets = allProducts[product.dexf]?.product.content?.asset as
      | IProductAssetModified[]
      | null
      | undefined;

    if (isValid && !dexfAssets) {
      missingIds.push(product.dexf);
    }

    if (!isValid || !dexfAssets?.length) {
      /*
        By leaving modelURI as empty string when no asset is found
        Core will not add a ModelInfoComponent to the entity,
        but still allow for the Entity to be created.
      */
      product.modelURI = '';
      return product;
    }

    const assets = dexfAssets.filter(value => {
      if (
        customRangeFamily ||
        Object.values(RangeFamilyEnum).includes(value.rangeFamily as RangeFamilyEnum)
      ) {
        return `${value.name}.glb` === product.modelURI;
      }
      return null;
    });

    /*
      If there are assets we expect to fetch the latest asset for that product
      which is expected to be found at the end of the array.
    */
    const asset = assets?.[assets.length - 1];

    if (!asset) {
      return product;
    }

    product.modelURI = asset.url;
    return product;
  });
};

const parsedDexfModels = (
  allProducts: IShoppingProductsMap,
  catalogData: any,
  customRangeFamily?: string
) => {
  const missingIds: string[] = [];
  const catalogProducts = mapCatalogProducts(
    allProducts,
    catalogData,
    missingIds,
    customRangeFamily
  );
  if (missingIds.length) {
    captureMessage('No asset found from dexf', scope => {
      scope.setExtra('dexfIds', missingIds);
      return scope;
    });
  }

  catalogData.products = catalogProducts;
  return catalogData;
};

/**
 * Using dexf for the models, we need to get the url from the productData and replace it with the modelUrl inside the catalogs.
 */
export const dexfLinkedCatalogs = (productData: IShoppingProductsMap, dispatch: AppDispatch) => {
  const deepCopiedSkyttaMainCatalog: ICatalog = structuredClone(skyttaMainCatalog);
  const deepCopiedPaxPanelsCatalog: ICatalog = structuredClone(paxPanelsCatalog);

  const dexfLinkedSkyttaCatalog = parsedDexfModels(productData, deepCopiedSkyttaMainCatalog);
  const dexfLinkedPaxPanelsCatalog = parsedDexfModels(productData, deepCopiedPaxPanelsCatalog);

  dispatch(configurationAction.setSkyttaCatalogData(dexfLinkedSkyttaCatalog));
  dispatch(configurationAction.setPaxPanelsCatalogData(dexfLinkedPaxPanelsCatalog));

  return { dexfLinkedSkyttaCatalog, dexfLinkedPaxPanelsCatalog };
};

export const fetchProductsData = createAsyncThunk<IShoppingProductsMap, undefined, IThunkApi>(
  'products/setProducts',
  async (_, { dispatch }) => {
    const productData = await getProducts();
    dispatch(productAction.setProducts(productData));

    const allValidSPRs = Object.values(productData).filter(item => {
      const { valid, content } = item.product;

      const isSpr = content?.itemType === ItemTypeEnum.spr;

      const hasContextProductPicture = !!content?.image?.find(img => {
        return img.typeName === ImageTypeNameEnum.contextProductPicture;
      });

      const vpcCode = getVpcForSpr(item.product);

      return valid && isSpr && hasContextProductPicture && vpcCode;
    });

    const sortedSPRs = randomizeList(allValidSPRs);

    dispatch(productAction.setAllValidSPRs(sortedSPRs));

    return productData;
  }
);

export const handleVisibleProducts = createAsyncThunk<void, IShoppingProduct[], IThunkApi>(
  'products/setVisibleProducts',
  async (visibleProducts, { dispatch }) => {
    const toTypes = shoppingProducts2NormalizedTypes(visibleProducts);

    dispatch(productAction.setVisibleProducts(toTypes));
  }
);
